import React, { useRef } from 'react';
import './ChatGPTPresetDrawer.scss';
import { injectIntl } from 'react-intl';
import { Button, Typography, IconButton, useMediaQuery } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import CloseIcon from '@mui/icons-material/Close';
import { tracking } from '../../../utils/tracking';
import { connect } from 'react-redux';
import { getTeamAiBotLogo, getTeamAiBotName } from '../../../utils/teamAiSetting/teamAiSetting';
import { bottomSheetOwnsScrollLock } from '../../../utils/scrollLock';
import QuotaTypography from './QuotaTypography/QuotaTypography';

/**
 * Swipe right to dismiss — the way out a phone is otherwise short of.
 *
 * The only exits these drawers had were the header ✕ and, on a keyboard,
 * Escape. Both want aim, and the ✕ is a 24px target in the corner furthest
 * from a thumb. This drawer enters from the right, so pushing it back that way
 * is the gesture the platform has already taught.
 *
 * Same numbers and same shape as ClientV2's client panel
 * (partials/ClientDetailDrawer.tsx), so one gesture means one thing across the
 * app — and a DETECTOR rather than a drag, for the same reason it is one
 * there: the panel does not follow the finger, it just plays the exit MUI
 * already plays for ✕. That keeps this to three handlers and no animation code.
 *
 * Duplicated rather than imported: the original lives inside a 3,000-line
 * ClientV2 component alongside its own tap-to-dismiss and met-counter state,
 * and lifting it out is a change to a heavily-tested file this work does not
 * otherwise touch. A shared hook is where these two belong eventually.
 */
const SWIPE_MIN_X = 56;
const SWIPE_MAX_MS = 600;
// How far the finger may drift down before it counts as scrolling rather than
// swiping — the drawer body scrolls, and that gesture must survive a wobble.
const SWIPE_MAX_DRIFT_Y = 24;

const ChatGPTPresetDrawer = props => {
  const {
    intl,
    open,
    onClose,
    onExited,
    onFeedback,
    children,
    overlay,
    bodyRef,
    teamAiSettings,
    title,
    // One short line under the title saying what this AI does, in the fixed
    // shape 「輸入 → 輸出」. A drawer is reached by tapping a card whose own
    // description then scrolls out of sight behind it, so the name was until
    // now the only thing an adviser had once inside.
    //
    // OPTIONAL, and deliberately so: pass nothing and the header renders
    // exactly as it did before this prop existed, which is what lets the eight
    // drawers adopt it one at a time rather than in a single commit.
    subtitle,
    // The remaining AI generations, in the one place every drawer keeps it.
    //
    // It used to live in each drawer's body, in nine different positions — one
    // above the buttons, one beside a 「跟進」 label, one under the results — so
    // an adviser asking "how many have I got left" had to find it again in each
    // drawer, and in 方案配對 V1 could not: that one spent quota and showed none.
    //
    // Read here rather than passed in, because `getGPTQuota` is a localStorage
    // read with no argument and no side effect. This re-renders whenever the
    // drawer around it does — which is what happens after a generation, since
    // `decreaseGPTQuota` is followed by the drawer's own force-update.
    showQuota
  } = props;

  const isWideScreen = useMediaQuery(theme => theme.breakpoints.up('sm'));

  const feedback = () => {
    tracking('Click on Feedback - ChatGPT');
    if (onFeedback) {
      onFeedback();
    }
  };

  const aiLogoSRC = getTeamAiBotLogo(teamAiSettings);
  const defaultBotName = getTeamAiBotName(teamAiSettings, intl.formatMessage({ id: 'chat-gpt-preset-dialog-title' }));
  const displayTitle = title || defaultBotName;

  /** The panel, so a gesture can walk up from its target and stop there. */
  const panelRef = useRef(null);
  /** The finger currently down, while it could still end in a dismissal. */
  const swipeRef = useRef(null);

  /**
   * Does this touch belong to something that consumes horizontal movement?
   *
   * Two kinds. A side-scroller — walked up to the panel, same test ClientV2
   * uses. And a text field, which matters far more HERE than it does there:
   * these drawers exist to be typed into, and a horizontal drag inside an
   * input is how a phone moves the caret and extends a selection. Pulling the
   * drawer out from under that would lose what the adviser was editing.
   */
  const startsWhereSwipeMeansSomethingElse = node => {
    let element = node instanceof Element ? node : null;
    if (element?.closest('input, textarea, [contenteditable="true"]')) return true;
    while (element && element !== panelRef.current) {
      if (element.scrollWidth - element.clientWidth > 4) {
        const { overflowX } = window.getComputedStyle(element);
        if (overflowX === 'auto' || overflowX === 'scroll') return true;
      }
      element = element.parentElement;
    }
    return false;
  };

  const handleTouchStart = event => {
    swipeRef.current = null;
    if (event.touches.length !== 1) return;
    // Portalled pickers and loading overlays bubble through the React tree,
    // even though they sit outside this panel. Their gestures belong to them.
    if (!panelRef.current?.contains(event.target)) return;
    if (startsWhereSwipeMeansSomethingElse(event.target)) return;
    const touch = event.touches[0];
    swipeRef.current = { x: touch.clientX, y: touch.clientY, at: Date.now() };
  };

  const handleTouchMove = event => {
    const start = swipeRef.current;
    if (!start) return;
    if (event.touches.length !== 1) {
      swipeRef.current = null;
      return;
    }
    const touch = event.touches[0];
    const driftY = Math.abs(touch.clientY - start.y);
    // The moment the finger commits to vertical this is a scroll of the body,
    // and can no longer become a dismissal however it ends.
    if (driftY > SWIPE_MAX_DRIFT_Y && driftY > Math.abs(touch.clientX - start.x)) swipeRef.current = null;
  };

  const handleTouchEnd = event => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start || !onClose) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const movedX = touch.clientX - start.x;
    const movedY = touch.clientY - start.y;
    // Rightward, decisively horizontal, and quick enough to read as a flick
    // rather than as a finger that wandered while resting on the panel.
    if (movedX < SWIPE_MIN_X) return;
    if (movedX <= Math.abs(movedY) * 2) return;
    if (Date.now() - start.at > SWIPE_MAX_MS) return;
    onClose();
  };

  return (
    <Drawer
      className="chat-gpt-preset-drawer"
      anchor="right"
      open={open}
      onClose={onClose}
      // AI drawers can be launched from a react-spring-bottom-sheet. That sheet
      // already owns the body lock; a second MUI lock can remember
      // `overflow: hidden` and restore it after the sheet has closed.
      //
      // Asked of the DOM rather than of the viewport width. Width answers "could
      // there be a sheet", which is not the same question: this drawer is opened
      // from plenty of surfaces that are not sheets at all (ChatGPTOptions,
      // clientFactsheet, PolicyReviewControls, EmergencyExportView...), and
      // standing the lock down for every phone-sized open let the page behind
      // scroll — the drawer's own content chains its overscroll straight into the
      // body, so an adviser who read to the end of an answer came back to a page
      // that had moved under them.
      //
      // Read during render, which is where MUI needs it: `disableScrollLock` is
      // consumed once, by `handleMounted` on the opening edge (Modal/useModal.js),
      // and ignored for the rest of the open. So the value that matters is the one
      // from the render where `open` turns true — by which point an already-open
      // sheet is committed to the DOM and this sees it.
      disableScrollLock={bottomSheetOwnsScrollLock()}
      slotProps={{
        transition: { onExited: onExited }
      }}
      // Launched from within dialogs (e.g. the meeting note dialog), so it must
      // sit above the modal layer (default Drawer z-index 1200 < Dialog 1300).
      sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
    >
      <div
        ref={panelRef}
        // On the panel rather than the scrolling body, so the header counts too
        // — it is the one strip of this drawer with nothing to swipe over.
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          width: isWideScreen ? '550px' : '90vw',
          maxWidth: isWideScreen ? '85vw' : '95vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        <div style={{ borderBottom: '1px solid #D3D3D3', padding: 6, width: '100%', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            {/* minWidth so this group may shrink below its content: without it
                a flex item refuses to, and the subtitle's ellipsis below can
                never engage — it would push the 意見反饋 button off instead.
                flex so it also GROWS, which is what gives the quota a right
                edge to sit against — the header's, stopping short of 意見反饋. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
              <img
                src={aiLogoSRC}
                alt="A.I. Pro"
                width={24}
                height={24}
                style={{ verticalAlign: 'unset', padding: '1px', objectFit: 'contain' }}
              />
              {/* Column rather than a second row under the whole header, so the
                  subtitle lines up with the title instead of with the ✕. The
                  close button and logo stay vertically centred against the pair
                  (the parent's alignItems), and with no subtitle this collapses
                  to a single Typography in a div — the same box as before. */}
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <Typography>{displayTitle}</Typography>
                {/* One line, ellipsed (these read short in Chinese and long in
                    English — 產品配對's is three times the length translated).
                    Nothing is lost to the clipping: every drawer repeats its
                    instruction in full as the first thing in the body. */}
                {subtitle && (
                  <Typography variant="caption" color="textSecondary" style={{ lineHeight: 1.3 }} noWrap>
                    {subtitle}
                  </Typography>
                )}
              </div>
            </div>
            {/* The quota reads under 意見反饋 rather than beside the subtitle.
                It belongs to the right-hand column — both are about this
                drawer's generation, not about the client — and the header
                gains no height from it: the left column is already two lines
                wherever a subtitle exists, which is everywhere the quota is
                shown. Right-aligned so the number ends on the same edge as
                the button above it. */}
            {(onFeedback || showQuota) && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                {onFeedback && (
                  <Button color="primary" onClick={feedback} style={{ padding: '2px 8px' }}>
                    {intl.formatMessage({ id: 'Feedback' })}
                  </Button>
                )}
                {showQuota && <QuotaTypography variant="caption" style={{ lineHeight: 1.3 }} />}
              </div>
            )}
          </div>
        </div>
        <div
          ref={bodyRef}
          data-body-scroll-lock-ignore="true"
          style={{
            padding: 12,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {children}
        </div>
        {overlay}
        <div style={{ width: '100%', height: 'var(--ion-safe-area-bottom, 0px)' }} />
      </div>
    </Drawer>
  );
};

const container = connect(state => ({
  teamAiSettings: state.team.aiSettings
}))(ChatGPTPresetDrawer);
export default injectIntl(container);
